import { ErrorCode } from './RegistrationStore';
import { DebtInfo } from '../config/Registration';

export interface ApiErrorLike {
  status?: number;
  code?: string;
  message?: string;
  error?: string;
}

export const privateClassErrorMessage = (error: ApiErrorLike | string | null | undefined): string => {
  const value = typeof error === 'string' ? { code: error, message: error } : (error || {});
  const code = (value.code || '').toUpperCase();
  const status = Number(value.status);

  switch (code) {
    case 'NOT_ENOUGH_PRIVATE_COINS':
      return 'You need more Private Coins to book this class.';
    case 'TIME_SLOT_UNAVAILABLE':
    case 'TEACHER_UNAVAILABLE':
    case 'ROOM_UNAVAILABLE':
      return 'That time is no longer available. Choose another time.';
    case 'PRICE_CHANGED':
      return 'The price changed. Review the refreshed quote before booking.';
    case 'PRICING_INCOMPLETE':
      return 'Pricing is not available for this selection. Choose another teacher or contact the studio.';
    case 'IDEMPOTENCY_KEY_REUSED':
      return 'This booking request was already used. Please try again.';
    case 'CANCELLATION_DEADLINE_PASSED':
    case 'CANCELLATION_NOT_ALLOWED':
      return 'The free-cancellation window has closed. Please contact the studio.';
    case 'BOOKING_NOT_FOUND':
      return 'This booking could not be found. Refresh your bookings and try again.';
    case 'FEATURE_DISABLED':
      return 'Private classes are currently unavailable.';
    case 'INVALID_REQUEST':
      return 'The private class request is invalid. Review your selection and try again.';
    default:
      if (status === 401 || status === 403) {
        return 'Your session has expired or you no longer have access. Please sign in again.';
      }
      if (status === 404) {
        return 'This private class option is no longer available. Refresh and choose another option.';
      }
      if (status >= 500) {
        return 'Private classes are temporarily unavailable. Please try again shortly.';
      }
      return value.message || value.error || 'We could not complete this action. Please try again.';
  }
};

export const renderErrorMessageWithLink = (code: ErrorCode, studioHomepageStore?: string, debtInfo?: DebtInfo, currencySymbol?: string, customMessage?: string): { text: string; hasLink: boolean; linkUrl?: string, linkText?: string } => {
  // If there's a custom message, use it instead of the predefined error messages
  if (customMessage) {
    return { text: customMessage, hasLink: false };
  }
  switch (code) {
    case ErrorCode.USER_DOES_NOT_EXIST:
      return { text: 'Unknown phone number', hasLink: false };
    case ErrorCode.NO_TRAINING_TIMES:
      return { text: 'No available lessons for today!', hasLink: false };
    case ErrorCode.DEBT_FOUND:
      if (debtInfo) {
        const invoiceDate = debtInfo.invoiceDate;
        const invoiceAmount = debtInfo.invoiceAmount.toFixed(2);
        return { 
          text: `You have an unpaid package from ${invoiceDate} for ${currencySymbol}${invoiceAmount}. Before making new class check-ins, please make the payment or contact our admin.`, 
          hasLink: false 
        };
      }
      return { text: 'We haven\'t yet processed the payment for your package.\nPlease talk to our admin to make the registration.', hasLink: false };
    case ErrorCode.REGISTRATION_EXISTS_FOR_TRAINING:
      return { text: 'You have already been registered to this training!', hasLink: false };
    case ErrorCode.LEVEL_REQUIREMENT_NOT_MET:
      return { text: 'This class requires a higher level. Please choose another class.', hasLink: false };
    case ErrorCode.DUPLICATE_PHONE:
      return { text: 'This phone number is used by multiple users. Please contact our admin desk to make the registration.', hasLink: false };
    case ErrorCode.NO_PACKAGE:
    case ErrorCode.NOT_ENOUGH_COINS:
      return { 
        text: 'You don\'t have any active training package or not enough coins. Choose your preferred package', 
        hasLink: true, 
        linkUrl: '/packages-membership',
        linkText: 'in Packages & Membership'
      };
    case ErrorCode.PENDING_EARLY_BIRD:
      return {
        text: 'You have a pending early bird package. Activate it before choosing classes or events.',
        hasLink: true,
        linkUrl: '/packages-membership',
        linkText: 'Open Packages & Membership'
      };
    case ErrorCode.THROTTLING_PHONE:
      return { text: 'You have exceeded the maximum number of requests. Please try again in a few minutes.', hasLink: false };
    case ErrorCode.BACKEND_MESSAGE:
      return { text: customMessage, hasLink: false };
    default: 
      return { text: 'An error occurred, please try again or contact our studio', hasLink: false };
  }
}
